interface GlossarySection extends HTMLElement {
  dataset: DOMStringMap & { _glossaryInit?: string };
}

const setActive = (filter: HTMLElement, btn: HTMLElement): void => {
  filter.querySelectorAll<HTMLElement>('.glossary__filter-btn').forEach((b: HTMLElement): void => {
    b.removeAttribute('data-active');
  });
  btn.setAttribute('data-active', '');
};

const clearActive = (filter: HTMLElement): void => {
  filter.querySelectorAll<HTMLElement>('.glossary__filter-btn').forEach((b: HTMLElement): void => {
    b.removeAttribute('data-active');
  });
};

const initFilterAndSearch = (sectionEl: GlossarySection): void => {
  const filter: HTMLElement | null = sectionEl.querySelector<HTMLElement>('[data-glossary-filter]');
  const search: HTMLInputElement | null = sectionEl.querySelector<HTMLInputElement>('[data-glossary-search]');
  if (!filter) return;

  const sentinel: HTMLElement | null = sectionEl.querySelector<HTMLElement>('[data-sticky-sentinel]');

  filter.addEventListener('click', (e: MouseEvent): void => {
    const target: HTMLElement | null = e.target as HTMLElement | null;
    const btn: HTMLElement | null = target ? target.closest<HTMLElement>('[data-filter]') : null;
    if (!btn) return;
    setActive(filter, btn);
    const value: string = btn.dataset['filter'] || '';
    sectionEl.querySelectorAll<HTMLElement>('[data-glossary-group]').forEach((group: HTMLElement): void => {
      group.hidden = value !== 'all' && group.dataset['glossaryGroup'] !== value;
    });
    if (search) search.value = '';
    sectionEl.querySelectorAll<HTMLElement>('[data-glossary-card]').forEach((card: HTMLElement): void => {
      card.hidden = false;
    });

    const filterRect: DOMRect = filter.getBoundingClientRect();
    const btnRect: DOMRect = btn.getBoundingClientRect();
    const btnLeftInFilter: number = btnRect.left - filterRect.left + filter.scrollLeft;
    const targetScrollLeft: number = btnLeftInFilter - filterRect.width / 2 + btnRect.width / 2;
    filter.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' });

    if (sentinel) {
      const sentinelDocTop: number = sentinel.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > sentinelDocTop) {
        window.scrollTo({ top: sentinelDocTop + 1, behavior: 'smooth' });
      }
    }
  });

  if (!search) return;

  search.addEventListener('input', (): void => {
    const q: string = search.value.trim().toLowerCase();
    if (!q) {
      const allBtn: HTMLElement | null = filter.querySelector<HTMLElement>('[data-filter="all"]');
      if (allBtn) setActive(filter, allBtn);
    } else {
      clearActive(filter);
    }

    sectionEl.querySelectorAll<HTMLElement>('[data-glossary-group]').forEach((group: HTMLElement): void => {
      group.hidden = false;
    });
    sectionEl.querySelectorAll<HTMLElement>('[data-glossary-card]').forEach((card: HTMLElement): void => {
      const term: string = card.dataset['term'] || '';
      card.hidden = q !== '' && term.indexOf(q) === -1;
    });
    sectionEl.querySelectorAll<HTMLElement>('[data-glossary-group]').forEach((group: HTMLElement): void => {
      const visibleCards: NodeListOf<HTMLElement> = group.querySelectorAll<HTMLElement>(
        '[data-glossary-card]:not([hidden])'
      );
      const hasNoCards: boolean = !group.querySelector('[data-glossary-card]');
      if (q && (visibleCards.length === 0 || hasNoCards)) {
        group.hidden = true;
      }
    });
  });
};

const initSticky = (sectionEl: GlossarySection): void => {
  const sentinel: HTMLElement | null = sectionEl.querySelector<HTMLElement>('[data-sticky-sentinel]');
  const sticky: HTMLElement | null = sectionEl.querySelector<HTMLElement>('[data-glossary-sticky]');
  const topBlur: HTMLElement | null = document.querySelector<HTMLElement>('[data-glossary-top-blur]');
  if (!sentinel || !sticky) return;

  // Cache the announcement / header references once. They don't change while
  // the page is open, so re-querying the DOM on every scroll frame would just
  // burn cycles for the same answer.
  const announcementSection: HTMLElement | null =
    document.querySelector<HTMLElement>('#shopify-section-announcement-bar') ||
    document.querySelector<HTMLElement>('[id*="announcement-bar"]');
  const headerSection: HTMLElement | null = document.querySelector<HTMLElement>('.shopify-section-header');

  let lastOffset: number = -1;
  let currentStuckOffset: number = 0;
  const setHeaderOffset = (): void => {
    let offset: number = 0;
    if (announcementSection) {
      const bottom: number = announcementSection.getBoundingClientRect().bottom;
      offset = Math.max(offset, Math.round(bottom));
    }
    if (headerSection) {
      const bottom: number = headerSection.getBoundingClientRect().bottom;
      offset = Math.max(offset, Math.round(bottom));
    }
    offset = Math.max(0, offset);
    currentStuckOffset = offset;
    if (offset !== lastOffset) {
      lastOffset = offset;
      document.documentElement.style.setProperty('--glossary-sticky-top', offset + 'px');
    }
  };

  const updateStuckState = (): void => {
    const stuck: boolean = sentinel.getBoundingClientRect().top <= currentStuckOffset;
    sticky.toggleAttribute('data-stuck', stuck);
    if (topBlur) topBlur.toggleAttribute('data-active', stuck);
  };


  let scrollFrame: number | null = null;
  const onScroll = (): void => {
    if (scrollFrame !== null) return;
    scrollFrame = requestAnimationFrame((): void => {
      setHeaderOffset();
      updateStuckState();
      scrollFrame = null;
    });
  };

  setHeaderOffset();
  updateStuckState();
  window.addEventListener('resize', onScroll);
  window.addEventListener('scroll', onScroll, { passive: true });

  if (!('IntersectionObserver' in window)) return;

  const observer: IntersectionObserver = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]): void => {
      entries.forEach((_entry: IntersectionObserverEntry): void => {
        updateStuckState();
      });
    },
    { threshold: 0 }
  );
  observer.observe(sentinel);
};

const init = (sectionEl: GlossarySection | null): void => {
  if (!sectionEl || sectionEl.dataset._glossaryInit === 'true') return;
  sectionEl.dataset._glossaryInit = 'true';

  initFilterAndSearch(sectionEl);
  initSticky(sectionEl);
};

let sectionLoadBound: boolean = false;

const glossary = (): void => {
  document.querySelectorAll<GlossarySection>('[data-glossary-section]').forEach(init);

  if (sectionLoadBound) return;
  sectionLoadBound = true;
  document.addEventListener('shopify:section:load', (event: Event): void => {
    const target: Element | null = event.target as Element | null;
    if (!target) return;
    const section: GlossarySection | null = target.querySelector<GlossarySection>('[data-glossary-section]');
    if (section) init(section);
  });
};

export default glossary;
